import type { JSX } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FolhaDeBaixo } from './FolhaDeBaixo';
import { Icone, iconeDoModulo } from './Icone';
import { rotulo } from './formato';
import { rodandoComoApp } from './instalar';
import { abrirRelatar } from './Relatar';
import { Modulo, alcanca, itensDaEngrenagem } from './modulos';
import { enderecoDoModulo, estaAqui, iniciais } from './regrasDaMoldura';
import { useSessao } from './sessao';
import { NOME_TEMA, Tema } from './tema';

/**
 * O «Mais» do celular, numa folha que sobe (onda 308, spec 06 §5.3, D2).
 *
 * ── Por que folha, e não a gaveta ────────────────────────────────────
 *
 * A gaveta lateral abre do canto de cima, que é o ponto da tela mais longe
 * do polegar de quem segura o aparelho com uma mão só — e ela traz o mapa
 * INTEIRO do espaço, inclusive o que já está na barra. Vendo as duas lado a
 * lado no canvas, o Humberto escolheu a folha (D2): ela sobe do pé da tela,
 * onde o dedo já está, e traz só o que falta.
 *
 * ── O que ela tem, de cima para baixo ────────────────────────────────
 *
 *  1. A troca de espaço (Hub · LabManager · Campo), só para quem tem mais de
 *     um — os MESMOS links da faixa e da gaveta, que a `Moldura` escreve uma
 *     vez (`linksDosEspacos`) e passa em `espacos`.
 *  2. Os módulos do espaço que a pessoa alcança e que NÃO estão na barra, em
 *     ladrilhos de 64 px, três por linha, por grupo (Trabalho, Bancada,
 *     Saída, SGQ…). Quem decide quais é a `Moldura` (`grupos`): o menu do
 *     espaço, menos a barra.
 *  3. A conta — a engrenagem do desktop, que no celular sai do topo: o nome e
 *     o perfil; Aparência (o tema atual, que troca a cada toque); Entrar
 *     como… (com a permissão); os itens da engrenagem que a pessoa alcança;
 *     Exclusão definitiva (só quem tem); Instalar no celular (só fora do app
 *     instalado); Relatar um problema nesta tela (D4); Sair, por último.
 *
 * Na recusa (a tela é de um espaço que a pessoa não alcança), a `Moldura`
 * não passa grupo nenhum, e a folha fica só com a conta — sem ela a pessoa
 * não teria como sair.
 *
 * Tocar num ladrilho ou num item que leva a outra tela fecha a folha: a
 * troca de endereço já a fecharia, mas o ladrilho da tela em que a pessoa
 * já está não troca o endereço, e a folha ficaria aberta por cima dela.
 */
export function FolhaDoMais({
  aberta, aoFechar, espacos, grupos, tema, aoTrocarTema, aoEntrarComo,
}: {
  aberta: boolean;
  aoFechar(): void;
  /** Os links dos espaços, ou `null` para quem tem um só. */
  espacos: ReactNode | null;
  grupos: { grupo: string | null; itens: Modulo[] }[];
  tema: Tema;
  aoTrocarTema(): void;
  /** `null` para quem não pode vestir outra persona. */
  aoEntrarComo: (() => void) | null;
}): JSX.Element | null {
  const { eu, sair } = useSessao();
  const { pathname: onde } = useLocation();
  if (!aberta || !eu) return null;

  const nome = eu.nome ?? eu.perfil ?? '';
  /* A engrenagem do desktop mostra os seus itens a todos; a folha, só o que a
     pessoa alcança (`alcanca`, a regra do menu): o técnico que tocasse em
     «Usuários e acessos» receberia uma recusa. Os que não declaram permissão
     (Ajustes, Exportação) continuam para todos, como lá. */
  const daEngrenagem = itensDaEngrenagem().filter((m) => !!m.rota && alcanca(m, eu.permissoes));

  return (
    <FolhaDeBaixo aberta={aberta} titulo="Mais" aoFechar={aoFechar} id="folha-mais" classe="folha-mais">
      {espacos && (
        <nav className="espacos-folha" aria-label="Espaços">
          {espacos}
        </nav>
      )}

      {grupos.map((g, i) => {
        const idTitulo = g.grupo ? `mais-grupo-${i}` : undefined;
        return (
          <section key={g.grupo ?? `g${i}`} className="mais-grupo" aria-labelledby={idTitulo}>
            {g.grupo && <h3 id={idTitulo}>{g.grupo}</h3>}
            <div className="mais-ladrilhos">
              {g.itens.map((m) => {
                const aqui = estaAqui(onde, m);
                return (
                  <Link
                    key={m.chave}
                    to={enderecoDoModulo(m)}
                    className={`ladrilho-mais${aqui ? ' aqui' : ''}`}
                    aria-current={aqui ? 'page' : undefined}
                    onClick={aoFechar}
                  >
                    <Icone nome={iconeDoModulo(m.chave)} />
                    <span className="mais-rotulo">{m.titulo}</span>
                  </Link>
                );
              })}
            </div>
          </section>
        );
      })}

      <div className="mais-conta">
        <div className="mais-pessoa">
          <span className="mais-disco" aria-hidden="true">{iniciais(nome)}</span>
          <span className="mais-quem">
            <b>{nome}</b>
            <em>{rotulo(eu.perfil)}</em>
          </span>
        </div>

        {/* O nome do tema ATUAL, e não o do próximo: o item diz onde você
            está antes de dizer para onde vai (o mesmo da engrenagem). A
            folha fica aberta: a troca é a cada toque. */}
        <button type="button" className="mais-item" onClick={aoTrocarTema}>
          <Icone nome="tema" />
          <span className="mais-rotulo">Aparência</span>
          <em className="mais-valor">{NOME_TEMA[tema]}</em>
        </button>

        {aoEntrarComo && (
          <button type="button" className="mais-item" onClick={aoEntrarComo}>
            <Icone nome="pessoa" />
            <span className="mais-rotulo">Entrar como…</span>
          </button>
        )}

        {daEngrenagem.map((m) => (
          <Link key={m.chave} to={m.rota as string} className="mais-item" onClick={aoFechar}>
            <Icone nome={iconeDoModulo(m.chave)} />
            <span className="mais-rotulo">{m.titulo}</span>
          </Link>
        ))}

        {/* 0211 (RN-214): a porta de uma pessoa só, na homologação — fora do
            registro de módulos, como na engrenagem. */}
        {eu.exclusaoDefinitiva && (
          <Link to="/exclusao-definitiva" className="mais-item perigo" onClick={aoFechar}>
            <Icone nome={iconeDoModulo('exclusao_definitiva')} />
            <span className="mais-rotulo">Exclusão definitiva</span>
          </Link>
        )}

        {/* Quem já abriu pelo ícone do aplicativo não precisa instalar. */}
        {!rodandoComoApp() && (
          <Link to="/instalar" className="mais-item" onClick={aoFechar}>
            <Icone nome="instalar" />
            <span className="mais-rotulo">Instalar no celular</span>
          </Link>
        )}

        {/* O mesmo diálogo do botão flutuante, que o celular esconde (D4). A
            folha fecha ANTES: o Relatar é um `<dialog>` modal, e a folha
            aberta por baixo ficaria esperando um Esc que já não é dela. */}
        <button type="button" className="mais-item"
          onClick={() => { aoFechar(); abrirRelatar(); }}>
          <Icone nome="relatar" />
          <span className="mais-rotulo">Relatar um problema nesta tela</span>
        </button>

        {/* Sair fecha a lista, separado por uma régua: é a única ação da
            conta que a pessoa não desfaz com um toque. */}
        <button type="button" className="mais-item sair" onClick={sair}>
          <Icone nome="sair" />
          <span className="mais-rotulo">Sair</span>
        </button>
      </div>
    </FolhaDeBaixo>
  );
}
